import React, { useCallback, useRef, useState } from "react";
import { useReveal } from "../hooks/useScroll";
import Recaptcha from "../components/Recaptcha";

/* No named people on this page. Individuals are reachable through their own
   card at /c/<name>, which is what the printed QR codes point at, and through
   the shared address below. */

export default function Contact({ theme }) {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("");
  const captcha = useRef(null);
  const onCaptchaReady = useCallback((api) => {
    captcha.current = api;
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const recaptcha = captcha.current?.getResponse();
      if (!recaptcha) {
        setStatus("captcha");
        return;
      }
      const url =
        window.location.hostname === "localhost"
          ? "http://localhost:5001/api/send-email"
          : "/send-email.php";
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, recaptcha }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to send");
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
      captcha.current?.reset();
      setTimeout(() => setStatus(""), 6000);
    } catch (err) {
      setStatus("error");
      captcha.current?.reset();
      setTimeout(() => setStatus(""), 6000);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section className="sec sec--pad" id="contact" ref={ref} style={{ background: "var(--ink-2)" }}>
      <div className="shell">
        <div style={{ display: "grid", gap: "clamp(38px, 6vw, 72px)" }} className="contact__grid">
          <div>
            <div className="rv">
              <span className="eyebrow">Talk to us</span>
              <h2 className="h-lg" style={{ marginTop: 20, maxWidth: "14ch" }}>
                Bring us a moment you cannot currently see.
              </h2>
              <p className="lede" style={{ marginTop: 22 }}>
                A shelf, a stand, a machine, a listing, a route. Tell us what
                happens there and what you wish you knew about it, and we will
                show you the version of this running on it.
              </p>
            </div>

            <div className="reach rv" style={{ marginTop: 38 }}>
              <a className="reach__c" href="mailto:info@sipnscore.co.za">
                <span className="micro">Email</span>
                <b>info@sipnscore.co.za</b>
                <span>Anything at all. It reaches all of us.</span>
              </a>
              <div className="reach__c">
                <span className="micro">Where</span>
                <b>South Africa</b>
                <span>Built and hosted here, on Johannesburg time.</span>
              </div>
              <a className="reach__c" href="/demo/">
                <span className="micro">Before you write</span>
                <b>Run the demo</b>
                <span>Sixty seconds. It will make this conversation shorter.</span>
              </a>
            </div>
          </div>

          <div className="rv">
            <form className="form" onSubmit={submit}>
              <div className="field">
                <label htmlFor="cf-name">Name</label>
                <input id="cf-name" required value={form.name} onChange={set("name")}
                  autoComplete="name" placeholder="Who are we speaking to" />
              </div>
              <div className="field">
                <label htmlFor="cf-email">Email</label>
                <input id="cf-email" type="email" required value={form.email} onChange={set("email")}
                  autoComplete="email" placeholder="Where we reply" />
              </div>
              <div className="field">
                <label htmlFor="cf-msg">What are you trying to see</label>
                <textarea id="cf-msg" required value={form.message} onChange={set("message")}
                  placeholder="The channel, the brand, and the question you cannot answer today" />
              </div>

              <Recaptcha theme={theme} onReady={onCaptchaReady} />

              <button className="btn" type="submit" disabled={status === "sending"}
                style={{ opacity: status === "sending" ? 0.6 : 1 }}>
                {status === "sending" ? "Sending" : "Send it"}
              </button>

              {status === "sent" && (
                <p className="formnote ok">Got it. One of us will come back to you.</p>
              )}
              {status === "error" && (
                <p className="formnote bad">
                  That did not go through. Mail us directly on info@sipnscore.co.za.
                </p>
              )}
              {status === "captcha" && (
                <p className="formnote bad">Tick the box above first.</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
